<template>
  <main class="page-leads">
    <div class="container-wide">
      <DownloadChromePluginPanel v-if="false" />
      <LeadsHeadline :pageKind="pageKind" :kindPrefix="kindPrefix" />
      <mq-layout mq="lg">
        <LeadsFilters :pageKind="pageKind" :kindPrefix="kindPrefix" />
      </mq-layout>
      <LeadsGraphs v-show="show && !graphsHiddenOnScroll && showGraphsPermission" :pageKind="pageKind"
        :kindPrefix="kindPrefix" />
      <LeadsGrid :pageKind="pageKind" :kindPrefix="kindPrefix" @toggle-lead-edit="toggleLeadEdit"
        @toggle-client-edit="toggleClientEdit" @toggle-lead-history="toggleLeadHistory" />
    </div>
    <transition name="fade">
      <ClientsGridContentEdit v-if="clientEditVisible" @toggle-client-edit2="toggleClientEdit" />
      <LeadsGridContentEdit v-if="leadEditVisible" :mode="toggleLeadEditMode" @toggle-lead-edit2="toggleLeadEdit" />
      <LeadsGridContentHistory v-if="leadHistoryVisible" :pageKind="pageKind" @toggle-lead-history2="toggleLeadHistory" />
    </transition>
  </main>
</template>

<script lang="ts">

import axios, { CancelTokenSource } from "axios";
import moment from "moment";
import VueMq from "vue-mq";
import { Component, Vue, Watch } from "vue-property-decorator";
import { Action, Getter } from "vuex-class";
import i18n from "@/i18n";
import ClientsGridContentEdit from "@/components/Leads/ClientsGridContentEdit.vue";
import LeadsGridContentEdit from "@/components/Leads/LeadsGridContentEdit.vue";
import LeadsGridContentHistory from "@/components/Leads/LeadsGridContentHistory.vue";
import { ChannelType } from "@/models/campaigns/ChannelType";
import { LeadsPageViewModel, RefreshGridMode, ToggleLeadEditMode, PhonePopupData } from "@/models/leads/Lead";
import { DataSourceResult } from "@/models/DataSourceResult";
import { FilterCollection } from "@/models/FilterCollection";
import { CompanyPermission, LeadPermissionType } from "@/models/leads/CompanyPermission";
import { LeadStatistic, LeadStatisticExtended } from "@/models/leads/LeadStatistic";
import { LeadsPageKind } from "@/models/LeadsPageKind";
import { LeadStatus } from "@/models/leads/LeadStatus";
import { MetadataMaskyooStatus } from "@/models/MetadataMaskyooStatus";
import { ChannelMultiselectItem, MultiselectItem } from "@/models/MultiselectItem";
import { TableSettings, TableSettingsResponse, TableSettingsState } from "@/models/TableSettings";
import ApiService, { CommonMultiselectPayload, LeadsPayload } from "@/services/api/ApiService";
import HelperService from "@/services/HelperService";
import LeadService from "@/services/LeadService";
import DownloadChromePluginPanel from "../components/DownloadChromePluginPanel.vue";
import LeadsHeadline from "../components/Leads/LeadsHeadline.vue";
import LeadsFilters from "../components/Leads/LeadsFilters.vue";
import LeadsGraphs from "../components/Leads/LeadsGraphs.vue";
import LeadsGrid from "../components/Leads/LeadsGrid.vue";
import GridTableSettingsEn from "../data/grid-defaultsettings-en.json";
import GridTableSettingsHe from "../data/grid-defaultsettings-he.json";
import { Meta } from "../decorators/MetaDecorator";
import PromiseQueue from "../helpers/PromiseQueue";

Vue.use(VueMq, {
  breakpoints: {
    sm: 576,
    md: 992,
    lg: Infinity,
  },
  defaultBreakpoint: "sm",
});
const namespace = "leadsModule";

@Component({
  components: {
    DownloadChromePluginPanel,
    LeadsHeadline,
    LeadsFilters,
    LeadsGraphs,
    LeadsGrid,
    ClientsGridContentEdit,
    LeadsGridContentEdit,
    LeadsGridContentHistory
  },
})
export default class Leads extends Vue {

  @Meta
  myMetaConfig() {
    return {
      title: i18n.t(`${this.kindPrefix}.Title`).toString()
    };
  }

  //#region Vuex getters and actions

  @Getter("columnsFilters", { namespace }) columnsFilters!: FilterCollection;
  @Getter("datePeriod", { namespace }) datePeriod!: Date[];
  @Getter("graphsHiddenOnScroll", { namespace }) graphsHiddenOnScroll!: boolean;
  @Getter("leads", { namespace }) leads!: DataSourceResult;
  @Getter("leadsCancelTokenSource", { namespace }) leadsCancelTokenSource!: CancelTokenSource | undefined;
  @Getter("clientsDatePeriod", { namespace }) clientsDatePeriod!: Date[];
  @Getter("clientsSearchValue", { namespace }) clientsSearchValue!: string;
  @Getter("clientsSelectedAdditionalStatusesIds", { namespace }) clientsSelectedAdditionalStatusesIds!: number[];
  @Getter("clientsSelectedAdGroupsIds", { namespace }) clientsSelectedAdGroupsIds!: string[];
  @Getter("clientsSelectedAdTitles", { namespace }) clientsSelectedAdTitles!: string[];
  @Getter("clientsSelectedAssetsIds", { namespace }) clientsSelectedAssetsIds!: string[];
  @Getter("clientsSelectedAssigneesIds", { namespace }) clientsSelectedAssigneesIds!: number[];
  @Getter("clientsSelectedCallStatusesIds", { namespace }) clientsSelectedCallStatusesIds!: number[];
  @Getter("clientsSelectedChannelMetadataTypesIds", { namespace }) clientsSelectedChannelMetadataTypesIds!: number[];
  @Getter("clientsSelectedChannelsIds", { namespace }) clientsSelectedChannelsIds!: number[];
  @Getter("clientsSelectedCompaniesIds", { namespace }) clientsSelectedCompaniesIds!: number[];
  @Getter("clientsSelectedCommitmentsIds", { namespace }) clientsSelectedCommitmentsIds!: number[];
  @Getter("clientsSelectedProcessesIds", { namespace }) clientsSelectedProcessesIds!: number[];
  @Getter("processes", { namespace }) processes!: MultiselectItem[];
  @Getter("clientsSelectedFoundMetadataTypesIds", { namespace }) clientsSelectedFoundMetadataTypesIds!: number[];
  @Getter("clientsSelectedLeadStatusesIds", { namespace }) clientsSelectedLeadStatusesIds!: LeadStatus[];
  @Getter("clientsSelectedPriceType", { namespace }) clientsSelectedPriceType!: string;
  @Getter("clientsSelectedSaleStatusesIds", { namespace }) clientsSelectedSaleStatusesIds!: LeadStatus[];
  @Getter("clientsSelectedTags", { namespace }) clientsSelectedTags!: string[];
  @Getter("leadsPageData", { namespace }) leadsPageData!: LeadsPageViewModel;
  @Getter("needToRefreshLeadsGrid", { namespace }) needToRefreshLeadsGrid!: RefreshGridMode;
  @Getter("page", { namespace }) page!: number;
  @Getter("searchValue", { namespace }) searchValue!: string;
  @Getter("selectedAdditionalStatusesIds", { namespace }) selectedAdditionalStatusesIds!: number[];
  @Getter("selectedAdGroupsIds", { namespace }) selectedAdGroupsIds!: string[];
  @Getter("selectedAdTitles", { namespace }) selectedAdTitles!: string[];
  @Getter("selectedAssetsIds", { namespace }) selectedAssetsIds!: string[];
  @Getter("selectedAssigneesIds", { namespace }) selectedAssigneesIds!: number[];
  @Getter("selectedCallStatusesIds", { namespace }) selectedCallStatusesIds!: number[];
  @Getter("selectedChannelMetadataTypesIds", { namespace }) selectedChannelMetadataTypesIds!: number[];
  @Getter("selectedChannelsIds", { namespace }) selectedChannelsIds!: number[];
  @Getter("selectedCompaniesIds", { namespace }) selectedCompaniesIds!: number[];
  @Getter("selectedCommitmentsIds", { namespace }) selectedCommitmentsIds!: number[];
  @Getter("selectedProcessesIds", { namespace }) selectedProcessesIds!: number[];
  @Getter("selectedFoundMetadataTypesIds", { namespace }) selectedFoundMetadataTypesIds!: number[];
  @Getter("selectedLeadStatusesIds", { namespace }) selectedLeadStatusesIds!: LeadStatus[];
  @Getter("selectedPriceType", { namespace }) selectedPriceType!: string;
  @Getter("selectedSaleStatusesIds", { namespace }) selectedSaleStatusesIds!: LeadStatus[];
  @Getter("selectedTags", { namespace }) selectedTags!: string[];
  @Getter("showDeleted", { namespace }) showDeleted!: boolean;
  @Getter("showDeletedClientLeads", { namespace }) showDeletedClientLeads!: boolean;
  @Getter("showFavourite", { namespace }) showFavourite!: boolean;
  @Getter("showFavouriteClientLeads", { namespace }) showFavouriteClientLeads!: boolean;
  @Getter("showGraphs", { namespace }) showGraphs!: boolean;
  @Getter("showGraphsClientLeads", { namespace }) showGraphsClientLeads!: boolean;
  @Getter("showNotActiveCampaign", { namespace }) showNotActiveCampaign!: boolean;
  @Getter("sortDir", { namespace }) sortDir!: string;
  @Getter("sortField", { namespace }) sortField!: string;
  @Getter("tags", { namespace }) tags!: MultiselectItem[];
  @Getter("uniqueByCampaign", { namespace }) uniqueByCampaign!: boolean;
  @Action("getChannels", { namespace }) getChannels!: (payload: CommonMultiselectPayload) => Promise<ChannelMultiselectItem[]>;
  @Action("getCompanies", { namespace }) getCompanies!: () => Promise<MultiselectItem[]>;
  @Action("getCommitments", { namespace }) getCommitments!: (payload: CommonMultiselectPayload) => Promise<MultiselectItem[]>;
  @Action("getProcesses", { namespace }) getProcesses!: (companyId: number) => Promise<MultiselectItem[]>;
  @Action("getLeads", { namespace }) getLeads!: (payload: LeadsPayload) => Promise<DataSourceResult>;
  @Action("getLeadsPageData", { namespace }) getLeadsPageData!: () => Promise<LeadsPageViewModel>;
  @Action("checkPhonePopup", { namespace }) checkPhonePopup!: () => Promise<PhonePopupData>;
  
  @Action("getTags", { namespace }) getTags!: (payload: CommonMultiselectPayload) => Promise<MultiselectItem[]>;
  @Action("setColumnsFilters", { namespace }) setColumnsFilters!: (columnsFilters: FilterCollection) => void;
  @Action("setDeactivateStats", { namespace }) setDeactivateStats!: (deactivateStats: boolean) => void;
  @Action("setClientsSelectedAssetsIds", { namespace }) setClientsSelectedAssetsIds!: (selectedAssetsIds: string[]) => void;
  @Action("setClientsSelectedCallStatusesIds", { namespace }) setClientsSelectedCallStatusesIds!: (selectedCallStatusesIds: number[]) => void;
  @Action("setClientsSelectedChannelsIds", { namespace }) setClientsSelectedChannelsIds!: (selectedChannelsIds: number[]) => void;
  @Action("setClientsSelectedChannelMetadataTypesIds", { namespace }) setClientsSelectedChannelMetadataTypesIds!: (selectedChannelMetadataTypesIds: number[]) => void;
  @Action("setClientsSelectedCompaniesIds", { namespace }) setClientsSelectedCompaniesIds!: (selectedCompaniesIds: number[]) => void;
  @Action("setClientsSelectedCommitmentsIds", { namespace }) setClientsSelectedCommitmentsIds!: (selectedCommitmentsIds: number[]) => void;
  @Action("setClientsSelectedProcessesIds", { namespace }) setClientsSelectedProcessesIds!: (selectedProcessesIds: number[]) => void;
  @Action("setClientsSelectedFoundMetadataTypesIds", { namespace }) setClientsSelectedFoundMetadataTypesIds!: (selectedFoundMetadataTypesIds: number[]) => void;
  @Action("setClientsSelectedLeadStatusesIds", { namespace }) setClientsSelectedLeadStatusesIds!: (selectedLeadStatusesIds: LeadStatus[]) => void;
  @Action("setClientsSelectedPriceType", { namespace }) setClientsSelectedPriceType!: (selectedPriceType: string) => void;
  @Action("setClientsSelectedSaleStatusesIds", { namespace }) setClientsSelectedSaleStatusesIds!: (selectedSaleStatusesIds: LeadStatus[]) => void;
  @Action("setClientsSelectedTags", { namespace }) setClientsSelectedTags!: (selectedTags: string[]) => void;
  @Action("setLeadsStatisticByAdditionalStatus", { namespace }) setLeadsStatisticByAdditionalStatus!: (data: LeadStatistic[] | null) => void;
  @Action("setLeadsStatisticByCallStatus", { namespace }) setLeadsStatisticByCallStatus!: (data: LeadStatistic[] | null) => void;
  @Action("setLeadsStatisticByCampaigns", { namespace }) setLeadsStatisticByCampaigns!: (data: LeadStatistic[] | null) => void;
  @Action("setLeadsStatisticByPhoneAndForms", { namespace }) setLeadsStatisticByPhoneAndForms!: (data: LeadStatistic[] | null) => void;
  @Action("setLeadsStatisticBySaleStatus", { namespace }) setLeadsStatisticBySaleStatus!: (data: LeadStatistic[] | null) => void;
  @Action("setLeadsStatisticChannelMetadataType", { namespace }) setLeadsStatisticChannelMetadataType!: (data: LeadStatistic[] | null) => void;
  @Action("setLeadsStatisticDealMeetingsLeads", { namespace }) setLeadsStatisticDealMeetingsLeads!: (data: LeadStatisticExtended[] | null) => void;
  @Action("setLeadsCancelTokenSource", { namespace }) setLeadsCancelTokenSource!: (cancelTokenSource: CancelTokenSource) => void;
  @Action("setStatsCancelTokenSource", { namespace }) setStatsCancelTokenSource!: (cancelTokenSource: CancelTokenSource) => void;
  @Action("setLeadsPayload", { namespace }) setLeadsPayload!: (leadsPayload: LeadsPayload) => void;
  @Action("setNeedToRefreshLeadsGrid", { namespace }) setNeedToRefreshLeadsGrid!: (needToRefreshLeadsGrid: RefreshGridMode) => void;
  @Action("setPage", { namespace }) setPage!: (page: number) => void;
  @Action("setSelectedCallStatusesIds", { namespace }) setSelectedCallStatusesIds!: (selectedCallStatusesIds: number[]) => void;
  @Action("setSelectedChannelsIds", { namespace }) setSelectedChannelsIds!: (selectedChannelsIds: number[]) => void;
  @Action("setSelectedChannelMetadataTypesIds", { namespace }) setSelectedChannelMetadataTypesIds!: (selectedChannelMetadataTypesIds: number[]) => void;
  @Action("setSelectedCompaniesIds", { namespace }) setSelectedCompaniesIds!: (selectedCompaniesIds: number[]) => void;
  @Action("setSelectedCommitmentsIds", { namespace }) setSelectedCommitmentsIds!: (selectedCommitmentsIds: number[]) => void;
  @Action("setSelectedProcessesIds", { namespace }) setSelectedProcessesIds!: (selectedProcessesIds: number[]) => void;
  @Action("setSelectedFoundMetadataTypesIds", { namespace }) setSelectedFoundMetadataTypesIds!: (selectedFoundMetadataTypesIds: number[]) => void;
  @Action("setSelectedLeadStatusesIds", { namespace }) setSelectedLeadStatusesIds!: (selectedLeadStatusesIds: LeadStatus[]) => void;
  @Action("setSelectedPriceType", { namespace }) setSelectedPriceType!: (selectedPriceType: string) => void;
  @Action("setSelectedSaleStatusesIds", { namespace }) setSelectedSaleStatusesIds!: (selectedSaleStatusesIds: LeadStatus[]) => void;
  @Action("setSelectedTags", { namespace }) setSelectedTags!: (selectedTags: string[]) => void;
  @Action("setShowDeleted", { namespace }) setShowDeleted!: (showDeleted: boolean) => void;
  @Action("setShowDeletedClientLeads", { namespace }) setShowDeletedClientLeads!: (showDeleted: boolean) => void;
  @Action("setShowFavourite", { namespace }) setShowFavourite!: (showFavourite: boolean) => void;
  @Action("setShowFavouriteClientLeads", { namespace }) setShowFavouriteClientLeads!: (showFavourite: boolean) => void;
  @Action("setShowGraphs", { namespace }) setShowGraphs!: (showGraphs: boolean) => void;
  @Action("setTableSettings", { namespace }) setTableSettings!: (tableSettings: TableSettings | undefined) => void;
  @Action("setUniqueByCampaign", { namespace }) setUniqueByCampaign!: (uniqueByCampaign: boolean) => void;

  //#endregion Vuex getters and actions


  //#region Properties

  MAX_LEADS_COUNT_FOR_STATS = 200000;
  PAGE_FILTERS_COOKIE_NAME = "page_filters";

  toggleLeadEditMode = ToggleLeadEditMode.None;
  token = "";

  public clientEditVisible = false;
  public leadEditVisible = false;
  public leadHistoryVisible = false;

  private refreshGridQueue = new PromiseQueue();
  
  //#endregion Properties


  //#region Watchers

  @Watch("datePeriod", { immediate: true })
  onDatePeriodChange(newDatePeriod: Date[], oldDatePeriod: Date[]): void {
    if (oldDatePeriod === undefined || newDatePeriod === undefined || oldDatePeriod.length === 0
      || moment(newDatePeriod[0]).format("YYYY-MM-DD") === moment(oldDatePeriod[0]).format("YYYY-MM-DD")
      && moment(newDatePeriod[1]).format("YYYY-MM-DD") === moment(oldDatePeriod[1]).format("YYYY-MM-DD")) {
      return;
    } else {
      this.refreshGrid();
    }
  }

  @Watch("clientsDatePeriod", { immediate: true })
  onClientsDatePeriodChange(newDatePeriod: Date[], oldDatePeriod: Date[]): void {
    if (oldDatePeriod === undefined || newDatePeriod === undefined || oldDatePeriod.length === 0
      || moment(newDatePeriod[0]).format("YYYY-MM-DD") === moment(oldDatePeriod[0]).format("YYYY-MM-DD")
      && moment(newDatePeriod[1]).format("YYYY-MM-DD") === moment(oldDatePeriod[1]).format("YYYY-MM-DD")) {
      return;
    } else {
      this.refreshGrid();
    }
  }

  @Watch("clientsSearchValue", { immediate: true })
  onClientsSearchValueChange(newValue: string, oldValue: string): void {
    if (oldValue === undefined) {
      return;
    } else {
      this.refreshGrid();
    }
  }

  @Watch("needToRefreshLeadsGrid", { immediate: true })
  onNeedToRefreshLeadsGrid(newValue: RefreshGridMode, oldValue: RefreshGridMode): void {
    if (oldValue === undefined) {
      return;
    } else if (newValue) {
      this.refreshGrid(newValue);
    }
  }

  @Watch("searchValue", { immediate: true })
  onSearchValueChange(newValue: string, oldValue: string): void {
    if (oldValue === undefined) {
      return;
    } else {
      this.refreshGrid();
    }
  }

  @Watch("pageKind", { immediate: true })
  onPageKindChange(newValue: LeadsPageKind, oldValue: LeadsPageKind): void {
    if (oldValue === undefined) {
      return;
    } else if (newValue !== undefined) {
      const filterCollection: FilterCollection = {
        filters: [],
      };
      this.setColumnsFilters(filterCollection);

      this.getTableSettings();
      this.refreshGridQueue.stop();
      this.refreshGrid();
      this.refreshGridQueue.start();
    }
  }

  @Watch("showDeleted", { immediate: true })
  onShowDeletedChange(newShowDeletedValue: string, oldShowDeletedValue: string): void {
    if (newShowDeletedValue === oldShowDeletedValue || oldShowDeletedValue === undefined) {
      return;
    } else {
      this.refreshGrid();
    }
  }

  @Watch("showDeletedClientLeads", { immediate: true })
  onShowDeletedClientLeadsChange(newShowDeletedValue: string, oldShowDeletedValue: string): void {
    if (newShowDeletedValue === oldShowDeletedValue || oldShowDeletedValue === undefined) {
      return;
    } else {
      this.refreshGrid();
    }
  }

  @Watch("showFavourite", { immediate: true })
  onShowFavouriteChange(newShowFavouriteValue: string, oldShowFavouriteValue: string): void {
    if (newShowFavouriteValue === oldShowFavouriteValue || oldShowFavouriteValue === undefined) {
      return;
    } else {
      this.refreshGrid();
    }
  }

  @Watch("showFavouriteClientLeads", { immediate: true })
  onShowFavouriteClientLeadsChange(newShowFavouriteValue: string, oldShowFavouriteValue: string): void {
    if (newShowFavouriteValue === oldShowFavouriteValue || oldShowFavouriteValue === undefined) {
      return;
    } else {
      this.refreshGrid();
    }
  }

  @Watch("showGraphs", { immediate: true })
  onShowGraphsChange(newValue: boolean, oldValue: boolean): void {
    if (newValue === oldValue || oldValue === undefined) {
      return;
    } else if (newValue) {
      this.refreshGrid(RefreshGridMode.GraphsOnly);
    }
  }

  @Watch("showGraphsClientLeads", { immediate: true })
  onShowGraphsClientLeadsChange(newValue: boolean, oldValue: boolean): void {
    if (newValue === oldValue || oldValue === undefined) {
      return;
    } else if (newValue) {
      this.refreshGrid(RefreshGridMode.GraphsOnly);
    }
  }

  @Watch("uniqueByCampaign", { immediate: true })
  onUniqueByCampaignChange(newUniqueValue: string, oldUniqueValue: string): void {
    if (newUniqueValue === oldUniqueValue || oldUniqueValue === undefined) {
      return;
    } else {
      this.refreshGrid();

      const cookieName = "do_not_show_unique_popup";
      const cookieValue: string = Vue.$cookies.get(cookieName);
      if (newUniqueValue && cookieValue !== "true") {
        HelperService.showConfirmDialog(
          undefined,
          i18n.t("Leads.UniquePopup.Text").toString(),
          i18n.t("Leads.UniquePopup.DoNotShow").toString(),
          i18n.t("OKay").toString(),
          () => {
            Vue.$cookies.set(cookieName, true, "1y");
          }
        );
      }
    }
  }

  //#endregion Watchers


  //#region Getters

  get commonPayload(): CommonMultiselectPayload {
    return {
      companyIds: this.usedSelectedCompaniesIds,
      showNotActiveCamp: this.showNotActiveCampaign,
      metadataIds: this.usedSelectedChannelMetadataTypesIds,
      statusIds: this.usedSelectedSaleStatusesIds.map((i: LeadStatus) => i.toString()),
      channelIds: this.usedSelectedChannelsIds,
    };
  }

  get kindPrefix(): string {
    return LeadsPageKind[this.pageKind];
  }

  get pageKind(): LeadsPageKind {
    return LeadService.getLeadsPageKind(this.$route.query.pageKind);
  }

  get show(): boolean {
    if (this.pageKind) {
      return this.showGraphsClientLeads;
    }
    return this.showGraphs;
  }

  get showChannelMetadataType(): boolean {
    return this.leadsPageData && !this.leadsPageData?.ActiveRestictions?.some((i: CompanyPermission) => i.Restriction === LeadPermissionType.Source);
  }

  get showGraphsPermission(): boolean {
    return !this.leadsPageData?.ActiveRestictions?.some((i: CompanyPermission) => i.Restriction === LeadPermissionType.LeadsScreenCharts);
  }

  //#region Getters based on page kind

  get usedDatePeriod(): Date[] {
    switch (this.pageKind) {
      case LeadsPageKind.Clients:
        return this.clientsDatePeriod;
      case LeadsPageKind.Leads:
        return this.datePeriod;
      default:
        throw new Error(`Unexpected pageKind: ${this.pageKind}`);
    }
  }

  get usedSearchValue(): string {
    switch (this.pageKind) {
      case LeadsPageKind.Clients:
        return this.clientsSearchValue;
      case LeadsPageKind.Leads:
        return this.searchValue;
      default:
        throw new Error(`Unexpected pageKind: ${this.pageKind}`);
    }
  }

  get usedSelectedAdditionalStatusesIds(): number[] {
    switch (this.pageKind) {
      case LeadsPageKind.Clients:
        return this.clientsSelectedAdditionalStatusesIds;
      case LeadsPageKind.Leads:
        return this.selectedAdditionalStatusesIds;
      default:
        throw new Error(`Unexpected pageKind: ${this.pageKind}`);
    }
  }

  get usedSelectedAdGroupsIds(): string[] {
    switch (this.pageKind) {
      case LeadsPageKind.Clients:
        return this.clientsSelectedAdGroupsIds;
      case LeadsPageKind.Leads:
        return this.selectedAdGroupsIds;
      default:
        throw new Error(`Unexpected pageKind: ${this.pageKind}`);
    }
  }

  get usedSelectedAdTitles(): string[] {
    switch (this.pageKind) {
      case LeadsPageKind.Clients:
        return this.clientsSelectedAdTitles;
      case LeadsPageKind.Leads:
        return this.selectedAdTitles;
      default:
        throw new Error(`Unexpected pageKind: ${this.pageKind}`);
    }
  }

  get usedSelectedAssetsIds(): string[] {
    switch (this.pageKind) {
      case LeadsPageKind.Clients:
        return this.clientsSelectedAssetsIds;
      case LeadsPageKind.Leads:
        return this.selectedAssetsIds;
      default:
        throw new Error(`Unexpected pageKind: ${this.pageKind}`);
    }
  }

  get usedSelectedAssigneesIds(): number[] {
    switch (this.pageKind) {
      case LeadsPageKind.Clients:
        return this.clientsSelectedAssigneesIds;
      case LeadsPageKind.Leads:
        return this.selectedAssigneesIds;
      default:
        throw new Error(`Unexpected pageKind: ${this.pageKind}`);
    }
  }

  get usedSelectedCallStatusesIds(): number[] {
    switch (this.pageKind) {
      case LeadsPageKind.Clients:
        return this.clientsSelectedCallStatusesIds;
      case LeadsPageKind.Leads:
        return this.selectedCallStatusesIds;
      default:
        throw new Error(`Unexpected pageKind: ${this.pageKind}`);
    }
  }

  get usedSelectedChannelMetadataTypesIds(): number[] {
    switch (this.pageKind) {
      case LeadsPageKind.Clients:
        return this.clientsSelectedChannelMetadataTypesIds;
      case LeadsPageKind.Leads:
        return this.selectedChannelMetadataTypesIds;
      default:
        throw new Error(`Unexpected pageKind: ${this.pageKind}`);
    }
  }

  get usedSelectedChannelsIds(): number[] {
    switch (this.pageKind) {
      case LeadsPageKind.Clients:
        return this.clientsSelectedChannelsIds;
      case LeadsPageKind.Leads:
        return this.selectedChannelsIds;
      default:
        throw new Error(`Unexpected pageKind: ${this.pageKind}`);
    }
  }

  get usedSelectedCompaniesIds(): number[] {
    switch (this.pageKind) {
      case LeadsPageKind.Clients:
        return this.clientsSelectedCompaniesIds;
      case LeadsPageKind.Leads:
        return this.selectedCompaniesIds;
      default:
        throw new Error(`Unexpected pageKind: ${this.pageKind}`);
    }
  }

  get usedSelectedCommitmentsIds(): number[] {
    switch (this.pageKind) {
      case LeadsPageKind.Clients:
        return this.clientsSelectedCommitmentsIds;
      case LeadsPageKind.Leads:
        return this.selectedCommitmentsIds;
      default:
        throw new Error(`Unexpected pageKind: ${this.pageKind}`);
    }
  }

  get usedSelectedProcessesIds(): number[] {
    switch (this.pageKind) {
      case LeadsPageKind.Clients:
        return this.clientsSelectedProcessesIds;
      case LeadsPageKind.Leads:
        return this.selectedProcessesIds;
      default:
        throw new Error(`Unexpected pageKind: ${this.pageKind}`);
    }
  }

  get usedSelectedFoundMetadataTypesIds(): number[] {
    switch (this.pageKind) {
      case LeadsPageKind.Clients:
        return this.clientsSelectedFoundMetadataTypesIds;
      case LeadsPageKind.Leads:
        return this.selectedFoundMetadataTypesIds;
      default:
        throw new Error(`Unexpected pageKind: ${this.pageKind}`);
    }
  }

  get usedSelectedLeadStatusesIds(): LeadStatus[] {
    switch (this.pageKind) {
      case LeadsPageKind.Clients:
        return this.clientsSelectedLeadStatusesIds;
      case LeadsPageKind.Leads:
        return this.selectedLeadStatusesIds;
      default:
        throw new Error(`Unexpected pageKind: ${this.pageKind}`);
    }
  }

  get usedSelectedPriceType(): string {
    switch (this.pageKind) {
      case LeadsPageKind.Clients:
        return this.clientsSelectedPriceType;
      case LeadsPageKind.Leads:
        return this.selectedPriceType;
      default:
        throw new Error(`Unexpected pageKind: ${this.pageKind}`);
    }
  }

  get usedSelectedSaleStatusesIds(): LeadStatus[] {
    switch (this.pageKind) {
      case LeadsPageKind.Clients:
        return this.clientsSelectedSaleStatusesIds;
      case LeadsPageKind.Leads:
        return this.selectedSaleStatusesIds;
      default:
        throw new Error(`Unexpected pageKind: ${this.pageKind}`);
    }
  }

  get usedSelectedTags(): string[] {
    switch (this.pageKind) {
      case LeadsPageKind.Clients:
        return this.clientsSelectedTags;
      case LeadsPageKind.Leads:
        return this.selectedTags;
      default:
        throw new Error(`Unexpected pageKind: ${this.pageKind}`);
    }
  }

  //#endregion Getters based on page kind

  //#endregion Getters


  //#region Lifecycle

  mounted(): void {
    this.token = HelperService.guid();
    const cookieValue: string = Vue.$cookies.get(this.PAGE_FILTERS_COOKIE_NAME);
    if (!cookieValue) { // when cookie expires, clear filters
      LeadService.resetFilters(this.pageKind, false);
    }
    this.getLeadsPageData().then(async (i: LeadsPageViewModel) => {
      if (i.CompanyId) {
        this.setUsedSelectedCompaniesIds([i.CompanyId]);
        if (i.AgencyId) {
          this.getCompanies();
        }
        this.getProcesses(i.CompanyId);
      } else {
        await this.refreshGrid();
        this.getCompanies();
        this.getCommitments(this.commonPayload);
        this.getTags(this.commonPayload);
      }
      this.applyQueryParams();
    });
    this.getTableSettings();
    this.checkPhonePopup();
  }

  //#endregion Lifecycle


  //#region Handlers

  public popupBodyOff(): void {
    const scrollY = document.body.style.top;
    document.body.style.position = "";
    document.body.style.top = "";
    window.scrollTo(0, parseInt(scrollY || "0") * -1);
  }

  public popupBodyOn(): void {
    document.body.style.top = "-" + window.scrollY + "px";
    document.body.style.position = "fixed";
  }

  public toggleClientEdit(): void {
    this.clientEditVisible = !this.clientEditVisible;
    this.clientEditVisible ? this.popupBodyOn() : this.popupBodyOff();
  }

  public toggleLeadEdit(mode: ToggleLeadEditMode): void {
    this.toggleLeadEditMode = mode;
    this.leadEditVisible = !this.leadEditVisible;
    this.leadEditVisible ? this.popupBodyOn() : this.popupBodyOff();
  }

  public toggleLeadHistory(): void {
    this.leadHistoryVisible = !this.leadHistoryVisible;
    this.leadHistoryVisible ? this.popupBodyOn() : this.popupBodyOff();
  }

  //#endregion Handlers


  //#region Private methods

  private applyQueryParams() {
    if (this.$route.query.callStatuses) {
      this.setUsedSelectedCallStatusesIds((this.$route.query.callStatuses as string).split(",").map(i => +i));
    }
    if (this.$route.query.channelIds) {
      this.setUsedSelectedChannelsIds((this.$route.query.channelIds as string).split(",").map(i => +i));
    }
    if (this.$route.query.channelMetadataTypes) {
      this.setUsedSelectedChannelMetadataTypesIds((this.$route.query.channelMetadataTypes as string).split(",").map(i => +i));
    }
    if (this.$route.query.companyIds) {
      this.setUsedSelectedCompaniesIds((this.$route.query.companyIds as string).split(",").map(i => +i));
    }
    if (this.$route.query.deleted) {
      const deleted: number = +(this.$route.query.deleted as string);
      const value = deleted === 1 ? true : false;
      if (this.pageKind) {
        this.setShowDeletedClientLeads(value);
      } else {
        this.setShowDeleted(value);
      }
    }
    if (this.$route.query.favourite) {
      const favourite: number = +(this.$route.query.favourite as string);
      const value = favourite === 1 ? true : false;
      if (this.pageKind) {
        this.setShowFavouriteClientLeads(value);
      } else {
        this.setShowFavourite(value);
      }
    }
    if (this.$route.query.foundMetadataTypes) {
      this.setUsedSelectedFoundMetadataTypesIds((this.$route.query.foundMetadataTypes as string).split(",").map(i => +i));
    }
    if (this.$route.query.leadEntryStatuses) {
      this.setUsedSelectedLeadStatusesIds((this.$route.query.leadEntryStatuses as string).split(",").map(i => i as any as LeadStatus));
    }
    if (this.$route.query.priceType) {
      this.setUsedSelectedPriceType(this.$route.query.priceType as string);
    }
    if (this.$route.query.projectIds) {
      this.setUsedSelectedCommitmentsIds((this.$route.query.projectIds as string).split(",").map(i => +i));
    }
    if (this.$route.query.saleStatuses) {
      this.setUsedSelectedSaleStatusesIds((this.$route.query.saleStatuses as string).split(",").map(i => +i));
    }
    if (this.$route.query.tags) {
      this.setUsedSelectedTags((this.$route.query.tags as string).split(","));
    }
    if (this.$route.query.unique) {
      const unique: number = +(this.$route.query.unique as string);
      this.setUniqueByCampaign(unique === 1 ? true : false);
    }
  }

  /**
   * Leads.Phone is stored in the normal form Utils.FormatPhone produces: an Israeli local number
   * ("0544874733") or a NANP national number with no country code ("8025487118"). The grid now shows
   * US numbers as "+1 (802) 548-7118" (#4452), so a value copied out of the grid has to lose its
   * country code again before it is used as a "contains" filter, or the search silently returns
   * nothing. Mirrors stripIsraeliCountryCode / getNanpNationalNumber in wwwroot/js/uco-phone.js.
   */
  private toStoredPhoneForm(value: string): string {
    const digits = value.replace(/\D/g, "");

    if (digits.length === 11 && digits.startsWith("1")) {
      return digits.substring(1);
    }

    if ((digits.length === 11 || digits.length === 12) && digits.startsWith("972")) {
      const local = digits.substring(3);
      return local.startsWith("0") ? local : `0${local}`;
    }

    return digits;
  }

  private getTableSettings(): void {
    this.setTableSettings(undefined);
    ApiService.getTableSettings(i18n.locale, this.$route.path).then((data: TableSettingsResponse) => {
      if (data.settings.length > 0) {
        data.settings.forEach((settings: TableSettings) => {
          settings.StateObject = JSON.parse(settings.State);
          this.setTableSettings(settings);
        });
      } else {
        let settingsState: TableSettingsState = GridTableSettingsHe;
        switch (i18n.locale) {
          case "en-US":
            settingsState = GridTableSettingsEn;
            break;
          case "he-IL":
            settingsState = GridTableSettingsHe;
            break;
        }
        this.setTableSettings({
          Culture: i18n.locale,
          GridId: "leadsgrid",
          State: JSON.stringify(settingsState),
          StateObject: settingsState,
          Url: location.pathname
        });
      }
    });
  }

  private refreshGrid(mode: RefreshGridMode = RefreshGridMode.Full): Promise<void> {
    return this.refreshGridQueue.add(async () => {
      if (this.leadsCancelTokenSource) {
        this.leadsCancelTokenSource?.cancel();
      }
      this.setNeedToRefreshLeadsGrid(RefreshGridMode.None);
      if (mode !== RefreshGridMode.GridOnly) {
        this.setPage(1);
      }
      this.token = HelperService.guid();

      if (mode !== RefreshGridMode.GridOnly) {
        // show spinners on graphics early
        this.setLeadsStatisticByAdditionalStatus(null);
        this.setLeadsStatisticByCallStatus(null);
        this.setLeadsStatisticByCampaigns(null);
        this.setLeadsStatisticByPhoneAndForms(null);
        this.setLeadsStatisticBySaleStatus(null);
        this.setLeadsStatisticChannelMetadataType(null);
        this.setLeadsStatisticDealMeetingsLeads(null);
      }

      //#region Filters
      const filterCollection: FilterCollection = {
        logic: "and",
        filters: [],
      };
      // Local snapshots to reduce repeated getter access
      const usedSelectedCompaniesIds = this.usedSelectedCompaniesIds;
      const usedSelectedChannelsIds = this.usedSelectedChannelsIds;
      const usedSelectedChannelMetadataTypesIds = this.usedSelectedChannelMetadataTypesIds;
      const usedSelectedFoundMetadataTypesIds = this.usedSelectedFoundMetadataTypesIds;
      const usedSelectedCallStatusesIds = this.usedSelectedCallStatusesIds;
      const usedSelectedCommitmentsIds = this.usedSelectedCommitmentsIds;
      const usedSelectedProcessesIds = this.usedSelectedProcessesIds;
      const usedSelectedAssigneesIds = this.usedSelectedAssigneesIds;
      const usedSelectedTags = this.usedSelectedTags;
      const usedSelectedAssetsIds = this.usedSelectedAssetsIds;
      const usedSelectedAdTitles = this.usedSelectedAdTitles;
      const usedSelectedAdGroupsIds = this.usedSelectedAdGroupsIds;
      const usedSelectedLeadStatusesIds = this.usedSelectedLeadStatusesIds;
      const usedSelectedSaleStatusesIds = this.usedSelectedSaleStatusesIds;
      if (this.usedSearchValue) {
        const searchFilters: FilterCollection = {
          logic: "or",
          filters: [],
        };
        const phoneText = this.toStoredPhoneForm(this.usedSearchValue);
        if (/^[+\d\s().-]+$/.test(this.usedSearchValue.trim()) && phoneText.length > 0) {
          searchFilters.filters?.push({ field: "Phone", operator: "contains", value: phoneText });
        }
        else if (this.usedSearchValue.indexOf("@") > -1) {
          searchFilters.filters?.push({ field: "Email", operator: "contains", value: `'${this.usedSearchValue}'` });
        }
        else {
          searchFilters.filters?.push({ field: "FullName", operator: "contains", value: `'${this.usedSearchValue}'` });
          searchFilters.filters?.push({ field: "Phone", operator: "contains", value: `'${phoneText}'` });
        }
        filterCollection.filters?.push(searchFilters);
      }
      if (this.columnsFilters && this.columnsFilters.filters && this.columnsFilters.filters?.length > 0) {
        filterCollection.filters?.push(this.columnsFilters);
      }
      if (this.leadsPageData?.CompanyId) {
        filterCollection.filters?.push(HelperService.setFilterArray(["CompanyId"], [this.leadsPageData.CompanyId], "eq"));
      } else if (usedSelectedCompaniesIds.length) {
        filterCollection.filters?.push(HelperService.setFilterArray(["CompanyId"], usedSelectedCompaniesIds, "eq"));
      }
      if (usedSelectedChannelsIds.length) {
        filterCollection.filters?.push(HelperService.setFilterArray(["ChannelId"], usedSelectedChannelsIds, "eq"));
      }
      if (usedSelectedChannelMetadataTypesIds.length) {
        filterCollection.filters?.push(HelperService.setFilterArray(["MetadataTypeIdFromChannel"], usedSelectedChannelMetadataTypesIds, "eq"));
      }
      if (usedSelectedFoundMetadataTypesIds.length) {
        const foundSet = new Set<number>(usedSelectedFoundMetadataTypesIds);
        if (foundSet.has(-1)) {
          filterCollection.filters?.push(HelperService.setFilterArray(["MetadataTypeIdFromUrl"], [
            ChannelType.Phone, ChannelType.CrmCallCenter
          ], "neq", "and"));
        } else if (foundSet.has(ChannelType.Phone)) {
          filterCollection.filters?.push(HelperService.setFilterArray(["MetadataTypeIdFromUrl"], [
            ChannelType.Phone, ChannelType.CrmCallCenter
          ], "eq", "or"));
        } else {
          filterCollection.filters?.push(HelperService.setFilterArray(["MetadataTypeIdFromUrl"], usedSelectedFoundMetadataTypesIds, "eq"));
        }
      }

      if (usedSelectedCallStatusesIds.length) {
        const statusFilters: FilterCollection = {
          logic: "or",
          filters: [],
        };
        for (const status of usedSelectedCallStatusesIds) {
          if (status == -2) {
            statusFilters.filters?.push({
              logic: "and",
              filters: [
                {
                  field: "StatusCall",
                  operator: "isnull",
                },
                {
                  field: "MetadataTypeIdFromUrl",
                  operator: "neq",
                  value: ChannelType.Phone.toString(),
                },
              ],
            });
          } else if (status == -1) {
            statusFilters.filters?.push({
              logic: "or",
              filters: [
                {
                  field: "StatusCall",
                  operator: "eq",
                  value: MetadataMaskyooStatus.Answer.toString(),
                },
                {
                  field: "StatusCall",
                  operator: "eq",
                  value: MetadataMaskyooStatus.Callback.toString(),
                },
                {
                  field: "StatusCall",
                  operator: "eq",
                  value: MetadataMaskyooStatus.CallMeBackFinished.toString(),
                },
                {
                  field: "StatusCall",
                  operator: "eq",
                  value: MetadataMaskyooStatus.CallRouter.toString(),
                },
                {
                  field: "StatusCall",
                  operator: "eq",
                  value: MetadataMaskyooStatus.NotAnswer.toString(),
                },
              ],
            });
          } else if (status == -3) {
            statusFilters.filters?.push({
              logic: "and",
              filters: [
                {
                  field: "MetadataTypeIdFromUrl",
                  operator: "eq",
                  value: ChannelType.WhatsAppOrganic.toString(),
                },
                {
                  field: "StatusCall",
                  operator: "isnull",
                },
              ],
            });
          } else if (status == 0 || status == 1) {            
            statusFilters.filters?.push({
              logic: "and",
              filters: [               
                {
                  field: "StatusCall",
                  operator: "eq",
                  value: status.toString(),
                },
                {
                  logic: "or",
                  filters: [
                    {
                      field: "MetadataTypeIdFromUrl",
                      operator: "eq",
                      value: ChannelType.Phone.toString(),
                    },
                    {
                      field: "MetadataTypeIdFromUrl",
                      operator: "eq",
                      value: ChannelType.CrmCallCenter.toString(),
                    },
                  ],
                }
              ],
            });
          } else {
            statusFilters.filters?.push({
              logic: "and",
              filters: [
                {
                  field: "MetadataTypeIdFromUrl",
                  operator: "eq",
                  value: ChannelType.CrmCallCenter.toString(),
                },
                {
                  field: "StatusCall",
                  operator: "eq",
                  value: status.toString(),
                },
              ],
            });
          }
        }
        filterCollection.filters?.push(statusFilters);
      }

      if (usedSelectedCommitmentsIds.length) {
        filterCollection.filters?.push(HelperService.setFilterArray(["ProjectId"], usedSelectedCommitmentsIds, "eq"));
      }
      if (usedSelectedProcessesIds.length) {
        filterCollection.filters?.push(HelperService.setFilterArray(["ClientsProcessId"], usedSelectedProcessesIds, "eq"));
      }
      if (usedSelectedAssigneesIds.length) {
        filterCollection.filters?.push(HelperService.setFilterArray(["UserId"], usedSelectedAssigneesIds, "eq"));
      }
      if (usedSelectedTags.length) {
        const tagIdToTitle = new Map<number, string>();
        for (const t of this.tags) {
          tagIdToTitle.set((t.Id as any as number), t.Title);
        }
        const tagTitles: string[] = usedSelectedTags
          .map((i: number | string) => typeof i === "number" ? (tagIdToTitle.get(i) || "") : i)
          .filter((x: string) => x && x.length > 0) as string[];
        filterCollection.filters?.push(HelperService.setFilterArray(["Tag"], tagTitles, "contains"));
      }
      if (usedSelectedAssetsIds.length) {
        filterCollection.filters?.push(HelperService.setFilterArray(["AssetId"], usedSelectedAssetsIds, "eq"));
      }
      if (this.uniqueByCampaign) {
        filterCollection.filters?.push(HelperService.setFilterArray(["ClientStageIdOnCreate"], [LeadStatus.Doubled], "neq"));
      }
      if (this.pageKind ? this.showFavouriteClientLeads : this.showFavourite) {
        filterCollection.filters?.push(HelperService.setFilterArray(["Favourite"], ["true"], "eq"));
      }
      if (usedSelectedAdTitles.length) {
        let selectedAdTitlesAdGroups: string[] = [];
        const selectedAdTitlesAdIds: string[] = [];
        usedSelectedAdTitles.forEach((i: string) => {
          const selectedAdTitleValues: string[] = i.split(":");
          selectedAdTitlesAdGroups.push(selectedAdTitleValues[0]);
          selectedAdTitlesAdIds.push(selectedAdTitleValues[1]);
        });
        selectedAdTitlesAdGroups = selectedAdTitlesAdGroups.filter((i: string) => i && i !== "null");
        if (selectedAdTitlesAdGroups.length > 0) {
          filterCollection.filters?.push(HelperService.setFilterArray(["AdGroupIdExternal"], selectedAdTitlesAdGroups, "eq"));
        }
        filterCollection.filters?.push(HelperService.setFilterArray(["AdIdExternal"], selectedAdTitlesAdIds, "eq"));
      }
      if (usedSelectedAdGroupsIds.length) {
        const selectedAdTitlesAdGroups = usedSelectedAdGroupsIds.filter((i: string) => i && i !== "null");
        if (selectedAdTitlesAdGroups.length > 0) {
          filterCollection.filters?.push(HelperService.setFilterArray(["AdGroupIdExternal"], selectedAdTitlesAdGroups, "eq"));
        }
      }

      if (usedSelectedLeadStatusesIds.length) {
        const statusFilters: FilterCollection = {
          logic: "or",
          filters: [],
        };
        for (const status of usedSelectedLeadStatusesIds) {
          if (status == LeadStatus.Doubled) {
            statusFilters.filters?.push({
              field: "ClientStageIdOnCreate",
              operator: "eq",
              value: status.toString(),
            });
          } else {
            statusFilters.filters?.push({
              logic: "and",
              filters: [
                {
                  field: "ClientStageIdOnCreate",
                  operator: "neq",
                  value: LeadStatus.Doubled.toString(),
                },
              ],
            });
          }
        }
        filterCollection.filters?.push(statusFilters);
      }

      if (usedSelectedSaleStatusesIds.length) {
        const statusFilters: FilterCollection = {
          logic: "or",
          filters: [],
        };
        for (const status of usedSelectedSaleStatusesIds) {
          if (status === LeadStatus.InProcess) {
            statusFilters.filters?.push({
              field: "ClientStageId",
              operator: "eq",
              value: "4",
            });
          } else if (status === LeadStatus.Closed) {
            statusFilters.filters?.push({
              field: "ClientStageId",
              operator: "eq",
              value: "9",
            });
          } else if (status === LeadStatus.Meeting) {
            statusFilters.filters?.push({
              field: "ClientStageId",
              operator: "eq",
              value: "7",
            });
          } else if (status === LeadStatus.NotGood) {
            statusFilters.filters?.push({
              field: "ClientStageId",
              operator: "eq",
              value: "11",
            });
          } else if (status === LeadStatus.New) {
            statusFilters.filters?.push({
                field: "ClientStageId",
                operator: "eq",
                value: LeadStatus.New.toString(),
            });
          }
        }
        filterCollection.filters?.push(statusFilters);
      }

      if (this.usedSelectedAdditionalStatusesIds.length) {
        filterCollection.filters?.push(
          HelperService.setFilterArray(["ClientStatusId"], this.usedSelectedAdditionalStatusesIds, "eq")
        );
      }

      //#endregion Filters


      const filtersString: string = HelperService.getFiltersString(filterCollection);
      const leadsPayload: LeadsPayload = {
        dateFrom: this.usedDatePeriod[0] ? moment(this.usedDatePeriod[0]).format("YYYY-MM-DD") : undefined,
        dateTo: this.usedDatePeriod[1] ? moment(this.usedDatePeriod[1]).format("YYYY-MM-DD") : undefined,
        filter: filtersString,
        kind: this.pageKind,
        page: this.page,
        pageSize: LeadService.getPageSize(),
        priceType: this.usedSelectedPriceType,
        showDeleted: this.pageKind ? this.showDeletedClientLeads : this.showDeleted,
        sort: `${this.sortField}-${this.sortDir}`,
        token: this.token,
        unique: this.uniqueByCampaign,
      };
      this.setLeadsPayload(leadsPayload);
      if (mode === RefreshGridMode.GraphsOnly) {
        if (this.leads.Total && this.leads.Total > this.MAX_LEADS_COUNT_FOR_STATS) {  // do not show stats for large data
          this.setDeactivateStats(true);
          this.setShowGraphs(false);
        } else {
          this.setDeactivateStats(false);
          if (this.showGraphsPermission && this.showGraphs) {
            this.setStatsCancelTokenSource(axios.CancelToken.source());
            await this.refreshStatistics(leadsPayload, filtersString);
            this.setStatsCancelTokenSource(null as any);
          }
        }
      } else {
        // Start statistics in parallel; cancel after grid returns if too large
        let statsPromise: Promise<void> | undefined;
        let statsSource: CancelTokenSource | undefined;
        if (this.showGraphsPermission && this.show) {
          this.setDeactivateStats(false);
          statsSource = axios.CancelToken.source();
          this.setStatsCancelTokenSource(statsSource);
          statsPromise = this.refreshStatistics(leadsPayload, filtersString)
            .catch(() => { /* ignore cancellation/errors */ })
            .finally(() => {
              this.setStatsCancelTokenSource(null as any);
            });
        }

        let leadsResult: DataSourceResult | void;
        try {
          leadsResult = await this.getLeads(leadsPayload);
        } finally {
          this.setNeedToRefreshLeadsGrid(RefreshGridMode.None);
        }

        if (this.leads.Total && this.leads.Total > this.MAX_LEADS_COUNT_FOR_STATS) {  // do not show stats for large data
          this.setDeactivateStats(true);
          this.setShowGraphs(false);
          // cancel in-flight stats if started
          statsSource?.cancel();
        } else {
          this.setDeactivateStats(false);
          if (mode === RefreshGridMode.Full && this.showGraphsPermission && this.show) {
            if (!statsPromise) {
              statsSource = axios.CancelToken.source();
              this.setStatsCancelTokenSource(statsSource);
              statsPromise = this.refreshStatistics(leadsPayload, filtersString)
                .catch(() => { /* ignore cancellation/errors */ })
                .finally(() => {
                this.setStatsCancelTokenSource(null as any);
              });
            }
          }
        }

        if (statsPromise) {
          await statsPromise;
        }
      }
      Vue.$cookies.set(this.PAGE_FILTERS_COOKIE_NAME, true, "8h");
    });
  }

  private async refreshStatistics(leadsPayload: LeadsPayload, filtersString: string): Promise<void> {
    // Aggregate request: fetch all stats in one call and commit slices
    const allStats = await ApiService.getLeadsStatisticsAll(leadsPayload, this.leadsCancelTokenSource);
    this.setLeadsStatisticByAdditionalStatus(allStats?.additionalStatus ?? []);
    this.setLeadsStatisticByCallStatus(allStats?.callStatus ?? []);
    this.setLeadsStatisticByPhoneAndForms(allStats?.phoneAndForms ?? []);
    this.setLeadsStatisticBySaleStatus(allStats?.saleStatus ?? []);
    this.setLeadsStatisticByCampaigns(allStats?.campaigns ?? []);
    this.setLeadsStatisticDealMeetingsLeads(allStats?.dealMeetingsLeads ?? []);
    if (this.showChannelMetadataType) {
      this.setLeadsStatisticChannelMetadataType(allStats?.channelMetadataType ?? []);
    }
  }

  //#region Setters based on page kind

  setUsedSelectedCallStatusesIds(selectedCallStatusesIds: number[]): void {
    switch (this.pageKind) {
      case LeadsPageKind.Clients:
        this.setClientsSelectedCallStatusesIds(selectedCallStatusesIds);
        break;
      case LeadsPageKind.Leads:
        this.setSelectedCallStatusesIds(selectedCallStatusesIds);
        break;
    }
  }
  
  setUsedSelectedChannelsIds(selectedChannelsIds: number[]): void {
    switch (this.pageKind) {
      case LeadsPageKind.Clients:
        this.setClientsSelectedChannelsIds(selectedChannelsIds);
        break;
      case LeadsPageKind.Leads:
        this.setSelectedChannelsIds(selectedChannelsIds);
        break;
    }
  }

  setUsedSelectedChannelMetadataTypesIds(selectedChannelMetadataTypesIds: number[]): void {
    switch (this.pageKind) {
      case LeadsPageKind.Clients:
        this.setClientsSelectedChannelMetadataTypesIds(selectedChannelMetadataTypesIds);
        break;
      case LeadsPageKind.Leads:
        this.setSelectedChannelMetadataTypesIds(selectedChannelMetadataTypesIds);
        break;
    }
  }

  setUsedSelectedCompaniesIds(selectedCompaniesIds: number[]): void {
    switch (this.pageKind) {
      case LeadsPageKind.Clients:
        this.setClientsSelectedCompaniesIds(selectedCompaniesIds);
        break;
      case LeadsPageKind.Leads:
        this.setSelectedCompaniesIds(selectedCompaniesIds);
        break;
    }
  }

  setUsedSelectedCommitmentsIds(selectedCommitmentsIds: number[]): void {
    switch (this.pageKind) {
      case LeadsPageKind.Clients:
        this.setClientsSelectedCommitmentsIds(selectedCommitmentsIds);
        break;
      case LeadsPageKind.Leads:
        this.setSelectedCommitmentsIds(selectedCommitmentsIds);
        break;
    }
  }

  setUsedSelectedProcessesIds(selectedProcessesIds: number[]): void {
    switch (this.pageKind) {
      case LeadsPageKind.Clients:
        this.setClientsSelectedProcessesIds(selectedProcessesIds);
        break;
      case LeadsPageKind.Leads:
        this.setSelectedProcessesIds(selectedProcessesIds);
        break;
    }
  }

  setUsedSelectedFoundMetadataTypesIds(selectedFoundMetadataTypesIds: number[]): void {
    switch (this.pageKind) {
      case LeadsPageKind.Clients:
        this.setClientsSelectedFoundMetadataTypesIds(selectedFoundMetadataTypesIds);
        break;
      case LeadsPageKind.Leads:
        this.setSelectedFoundMetadataTypesIds(selectedFoundMetadataTypesIds);
        break;
    }
  }

  setUsedSelectedLeadStatusesIds(selectedLeadStatusesIds: LeadStatus[]): void {
    switch (this.pageKind) {
      case LeadsPageKind.Clients:
        this.setClientsSelectedLeadStatusesIds(selectedLeadStatusesIds);
        break;
      case LeadsPageKind.Leads:
        this.setSelectedLeadStatusesIds(selectedLeadStatusesIds);
        break;
    }
  }

  setUsedSelectedPriceType(selectedPriceType: string): void {
    switch (this.pageKind) {
      case LeadsPageKind.Clients:
        this.setClientsSelectedPriceType(selectedPriceType);
        break;
      case LeadsPageKind.Leads:
        this.setSelectedPriceType(selectedPriceType);
        break;
    }
  }

  setUsedSelectedSaleStatusesIds(selectedSaleStatusesIds: LeadStatus[]): void {
    switch (this.pageKind) {
      case LeadsPageKind.Clients:
        this.setClientsSelectedSaleStatusesIds(selectedSaleStatusesIds);
        break;
      case LeadsPageKind.Leads:
        this.setSelectedSaleStatusesIds(selectedSaleStatusesIds);
        break;
    }
  }

  setUsedSelectedTags(selectedTags: string[]): void {
    switch (this.pageKind) {
      case LeadsPageKind.Clients:
        this.setClientsSelectedTags(selectedTags);
        break;
      case LeadsPageKind.Leads:
        this.setSelectedTags(selectedTags);
        break;
    }
  }

  //#endregion Setters based on page kind

  //#endregion Private methods
}

</script>

<style lang="scss" scoped>
.page-leads {
  // @media (max-width: 1599px) {
  //   padding-top: 389px;
  //   @media (resolution: 120dpi) {
  //     padding-top: 271px;
  //   }
  // }
  // @media (max-width: 1279px) and (resolution: 120dpi) {
  //   padding-top: 389px;
  // }
  // @media (max-width: 991px) {
  //   padding-top: 162px;
  // }
  // @media (max-width: 359px) {
  //   padding-top: 191px;
  // }
}
</style>
